/* eslint-disable no-useless-constructor */
/* global DeferredMedia */

if (!customElements.get('product-model')) {
  class ProductModel extends DeferredMedia {
    constructor() {
      super();
    }

    loadContent(focus = true, pause = true, loadTrigger = 'click') {
      const wasLoaded = this.hasAttribute('loaded');
      let useAndroidModel = false;

      if (!wasLoaded) {
        const isIOS = /iPad|iPhone|iPod/i.test(navigator.userAgent)
          || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        const templateModelViewer = this.querySelector('template')?.content.querySelector('model-viewer');

        if (isIOS && templateModelViewer) {
          // Restore Shopify's native model-viewer markup on iOS. Safari was
          // able to load this model before custom poster/progress slots were
          // injected, and can stall near completion with those replacements.
          templateModelViewer.querySelector('.product-model__poster')?.remove();
          templateModelViewer.querySelector('.product-model__progress')?.remove();
          templateModelViewer.removeAttribute('camera-orbit');
          templateModelViewer.removeAttribute('max-camera-orbit');
          this.querySelector('.product-model__poster--scaled')?.classList.remove('product-model__poster--scaled');
        } else if (/Android/i.test(navigator.userAgent) && templateModelViewer?.dataset.androidSrc) {
          // The original Falcon model expands to hundreds of MB in GPU memory
          // and can lose the WebGL context on Android before its first frame
          // renders. Swap the inert template before it starts downloading.
          templateModelViewer.setAttribute('src', templateModelViewer.dataset.androidSrc);
          templateModelViewer.setAttribute('loading', 'eager');
          templateModelViewer.setAttribute('reveal', 'auto');
          templateModelViewer.setAttribute('interaction-prompt', 'none');
          useAndroidModel = true;
        }
      }

      super.loadContent(focus, pause, loadTrigger);

      // loadContent can be called by both the intersection observer and the
      // gallery. Only initialise the model viewer once.
      if (wasLoaded) return;

      const modelViewer = this.querySelector('model-viewer');
      if (useAndroidModel && modelViewer) {
        // Shopify's toggleable model markup uses an interaction-based reveal.
        // Android can remain near the end of loading while waiting for the
        // poster interaction. Explicitly reveal once the custom element is
        // ready so rendering can finish without another tap.
        customElements.whenDefined('model-viewer').then(() => {
          if (modelViewer.isConnected && typeof modelViewer.dismissPoster === 'function') {
            modelViewer.dismissPoster();
          }
        });
      }

      const progress = modelViewer?.querySelector('.product-model__progress');
      const progressFill = progress?.querySelector('.product-model__progress-fill');
      if (modelViewer && progress && progressFill) {
        let completionTimer;

        const finishLoading = () => {
          window.clearTimeout(completionTimer);
          progressFill.style.width = '100%';
          progress.classList.add('is-complete');
        };

        const handleError = (event) => {
          window.clearTimeout(completionTimer);
          // Keep model-viewer's poster visible, but do not leave customers with
          // a progress bar that can never complete.
          progress.classList.add('is-complete');
          console.error('Unable to load product model.', event.detail || event);
        };

        modelViewer.addEventListener('progress', (event) => {
          const value = Math.min(Math.max(event.detail?.totalProgress || 0, 0), 1);
          progressFill.style.width = `${value * 100}%`;

          // Some mobile devices finish downloading but stall while decoding
          // textures or creating the WebGL scene. Do not show 100% forever.
          if (value >= 0.999 && !completionTimer) {
            completionTimer = window.setTimeout(() => {
              handleError(new Error('Product model timed out after download.'));
            }, 15000);
          }
        });
        modelViewer.addEventListener('load', finishLoading, { once: true });
        modelViewer.addEventListener('error', handleError, { once: true });

        // A cached model may finish before the listeners above are attached.
        if (modelViewer.loaded) finishLoading();
      }

      Shopify.loadFeatures([
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: this.setupModelViewerUI.bind(this)
        }
      ]);
    }

    setupModelViewerUI(errors) {
      if (errors) return;
      const modelViewer = this.querySelector('model-viewer');
      if (!modelViewer) return;
      this.modelViewerUI = new Shopify.ModelViewerUI(modelViewer);
    }
  }

  customElements.define('product-model', ProductModel);
}

window.ProductModel = {
  loadShopifyXR() {
    Shopify.loadFeatures([
      {
        name: 'shopify-xr',
        version: '1.0',
        onLoad: this.setupShopifyXR.bind(this)
      }
    ]);
  },

  setupShopifyXR(errors) {
    if (errors) return;

    if (!window.ShopifyXR) {
      document.addEventListener('shopify_xr_initialized', () => this.setupShopifyXR());
      return;
    }

    document.querySelectorAll('[id^="product-json-"]').forEach((modelJSON) => {
      window.ShopifyXR.addModels(JSON.parse(modelJSON.textContent));
      modelJSON.remove();
    });

    window.ShopifyXR.setupXRElements();
  }
};

window.addEventListener('DOMContentLoaded', () => {
  if (window.ProductModel) window.ProductModel.loadShopifyXR();
});
